import {IReg, IRegPointer, IUiEnv, REG} from "lib/commons/registry";
import {MsgOver, OSkinableInit} from "back/commons/basis";
import {JSX} from "lib/commons/xml/dom";
import {AREAS, IArea, IControlAreaContext, IControlLabelContext} from "lib/commons/areas";
import {Button, OButtonInit} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {FORMS} from "lib/commons/forms";
import {BaseAreaViewAsync} from "lib/commons/views";
import {ICoreUniverseEnv} from "lib/core/universe";
import {ERROR} from "lib/core/errorReport";
import {IUserAreaContext, UserAccountInputArea} from "back/core/dialogs/userAreas";
import {EUserAspects, EUserType, JUser, USER, UsersSrv} from "lib/core/user";
import {DOMSH} from "lib/commons/xml/domsh";
import {USERSELF, UserSelfSrv} from "lib/core/userSelf";

/**
 * Écran de création d'un utilisateur / groupe
 */

export interface OUserCreateInit extends OSkinableInit {
	reg: IReg<ICoreUniverseEnv & IUiEnv>
}

abstract class UserCreateBase<E extends IRegPointer<ICoreUniverseEnv & IUiEnv>> extends BaseAreaViewAsync<E> implements IUserAreaContext, IControlLabelContext {
	reg: IReg<ICoreUniverseEnv & IUiEnv>;

	// IUserAreaContext
	uiContextSgn: string;
	userSelfSrv: UserSelfSrv
	usersSrv: UsersSrv

	// IControlLabelContext
	buildControlLabel = true;
	buildUnlockBtn = true;

	protected _form: HTMLFormElement;
	forcedUserDataOnSave: JUser = {};
	protected _emptyData: JUser;
	protected _saveBtn: Button;

	protected async _initialize(init: OUserCreateInit) {
		if (!this.reg) this.reg = REG.createSubReg(this.findReg(init));
		this.userSelfSrv = this.reg.env.universe.userSelf;
		this.usersSrv = this.reg.env.universe.adminUsers;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this.reg.installSkin("standard-dialog", sr);
		this.reg.installSkin("form-control-areas", sr);
		this.reg.installSkin("userscreate-core", sr);
		this._initAndInstallSkin(this.localName, init);

		this._saveBtn = <Button î={{disabled: true, label: "Ajouter", uiContext: "dialog"} as OButtonInit} class="default" onclick={this.onSave}/> as Button;
		const closeBtn = <Button î={{disabled: false, label: "Annuler", uiContext: "dialog"} as OButtonInit} onclick={this.onClose}/> as Button;

		this._form = sr.appendChild(<form id="form" autocomplete="off"/>) as HTMLFormElement;
		this.initForm();
		await AREAS.applyLayout(this._form, await Promise.all(this.reg.mergeLists<IArea<IUserAreaContext & IControlAreaContext> | Promise<IArea<IUserAreaContext & IControlAreaContext>>>("userProps:create", "userProps:create:" + this.id)), this);
		sr.appendChild(<div id="footer">
			{this._saveBtn}
			{closeBtn}
		</div>);

		this._emptyData = FORMS.formToJson(this._form);
		await this._refreshUi();
		this._form.checkValidity();

		let checFormOnInput = 0;
		this._form.addEventListener('input', () => {
			if (checFormOnInput) {
				clearTimeout(checFormOnInput);
				checFormOnInput = 0;
			}
			checFormOnInput = setTimeout(() => {this._refreshUi()}, 300) as any;
		});
	}

	/** Différents refresh UI du formulaire et des btns
	 * 	@param clean : raz du formulaire
	 */
	protected async _refreshUi(clear: boolean = false) {
		if (clear) FORMS.jsonToForm(this._emptyData, this._form);
		this._saveBtn.disabled = !(await this.isDirty());
	}

	/** Retourne true si le formulaire est dirty */
	protected async isDirty(): Promise<boolean> {
		const user = FORMS.formToJson(this._form);
		return JSON.stringify(user) !== JSON.stringify(this._emptyData);
	}

	/** Initialisation du formulaire (def du/des fieldset) */
	protected initForm(): void {
		this._form.appendChild(
			<fieldset id="fieldset">
				<div id="common">
					<div class="fields" area-ids="account isDisabled enabledEndDt isHidden groups"/>
					<div class="fields" area-ids="nickNames firstName lastName groupName email"/>
				</div>
				<div class="fields" area-ids="*"/>
			</fieldset>);
	}

	/**
	 * Action de validation
	 * @param ev
	 */
	protected async onSave(this: Button, ev: Event): Promise<void> {
		if (!this.disabled) {
			const me = DOMSH.findHost<UserCreateBase<E>>(this);
			if (me._form.reportValidity()) {
				const result = await new MsgOver().setCustomMsg("Veuillez patienter...", "info").showMsgOver(me).waitFor(
					me._doExecuteSave()
				)
				if (result) await me._doClose(result);
			}
			//else	POPUP.showNotifWarning("Veuillez corriger le formulaire.", this);
		}
	}

	/**
	 * Action de validation (sans contrôle déjà réalisé)
	 * @return account si le user a été créé, null dans le cas contraire
	 */
	protected abstract _doExecuteSave(): Promise<string | null>

	/**
	 * Action de fermeture / abandon
	 * @param ev
	 */
	protected async onClose(this: Button, ev: Event): Promise<void> {
		const me = DOMSH.findHost<UserCreateBase<E>>(this);
		//if (!await me.isDirty() || await POPUP.confirm("Voulez-vous abandonner la déclaration ?", me, {cancelLbl: "Reprendre", okLbl: "Abandonner"})) {
		return me._doClose();
		//}
	}

	/**
	 * Action de la fermeture
	 * @param ev
	 */
	async _doClose(returnValue?: any): Promise<void> {
		POPUP.findPopupableParent(this).close(returnValue);
	}

	onViewBeforeHide(close?: boolean): boolean {
		return false;
	}

	async onViewWaitForHide(close?: boolean): Promise<boolean> {
		return true;
	}
}

REG.reg.registerSkin('userscreate-core', 1, /* language=CSS */ `
	form {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		flex: 1;
		padding: 1em;
		overflow: auto;
	}

	fieldset {
		border: none;
		padding: 0;
		margin: 0;
		min-width: 0;
	}

	#common {
		display: flex;
		flex: 1;
		min-height: 0;
		min-width: 0;
		flex-wrap: wrap;
	}

	#common > .fields {
		flex: 1;
		display: grid;
		min-width: 10em;
		grid-template-columns: auto 1fr;
		grid-row-gap: .7rem;
		grid-auto-rows: max-content;
	}

	#common > .fields > div {
		display: contents;
	}

	#common > .fields:first-child {
		margin-inline-end: 2em;
	}

	.fields div[hidden] {
		display: none !important;
	}

	div[area-id=authMethod] {
		flex-direction: column;
	}

	div[area-id=authMethod] > div {
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	div[area-id="passwordSetMode"] > .subPanel {
		border: 0;
		max-width: fit-content;
	}

	.ctrlLbl > .ctrlLbl {
		margin: 0;
	}

	select {
		max-width: fit-content;
	}
`);

export class UserCreate<E extends IRegPointer<ICoreUniverseEnv>> extends UserCreateBase<E> {

	protected async _initialize(init: OUserCreateInit): Promise<void> {
		if (!this.reg) this.reg = REG.createSubReg(this.findReg(init));

		this.id = "users";
		this.uiContextSgn = "@user#create";
		this.forcedUserDataOnSave.userType = EUserType.user;
		// Form
		this.reg.addToList("userProps:create:users", 'account', 1, new UserAccountInputArea());
		this.reg.addSvcToList("userProps:create:users", 'isDisabled', 1, 'area.users.isDisabled.users');
		if (this.reg.env.universe.adminUsers.hasAspect(EUserAspects.enabledEndDt)) this.reg.addSvcToList("userProps:create:users", 'enabledEndDt', 1, 'area.users.enabledEndDt');
		this.reg.addSvcToList("userProps:create:users", 'isHidden', 1, 'area.users.isHidden.users');
		this.reg.addSvcToList("userProps:create:users", 'nickNames.first', 1, 'area.users.nickNames.first');
		this.reg.addSvcToList("userProps:create:users", 'firstName', 1, 'area.users.firstName');
		this.reg.addSvcToList("userProps:create:users", 'lastName', 1, 'area.users.lastName');
		this.reg.addSvcToList("userProps:create:users", 'groups', 1, 'area.users.groupsmembers.users.create');
		this.reg.addSvcToList("userProps:create:users", 'grantedRoles', 1, 'area.users.grantedRoles.users');
		this.reg.addSvcToList("userProps:create:users", 'email', 1, 'area.users.email.user');
		return super._initialize(init);
	}

	protected async _doExecuteSave(): Promise<string | null> {
		try {
			const params = Object.assign(FORMS.formToJson(this._form), this.forcedUserDataOnSave);
			let userName = USER.getPrimaryName(params);
			const resp = await this.userSelfSrv.createUser(params);
			let errorStr = USERSELF.tcheckUserSelfRespError(resp, "");
			if (errorStr === null) {
				if (resp.result === 'validationPending')
					POPUP.showNotifWarning(`L'utilisateur '${userName}' a été créé avec succès.\nRemarque : ce compte est en l'état inactif.`, this);
				else
					POPUP.showNotifInfo(`L'utilisateur '${userName}' a été créé avec succès.`, this);
				return params.account || true;//return true, contexte account posé automatiquement par ex dans Distrib
			} else
				await ERROR.show(errorStr ? `La création de l'utilisateur '${userName}' a échoué. ${errorStr}` : "La création de l'utilisateur a échoué.", new Error(JSON.stringify(resp)));
			return null;
		} catch (e) {
			await ERROR.report(`La création de l'utilisateur a échoué. Veuillez réessayer ultérieurement.`, e);
			return null;
		}
	}
}

customElements.define("usercreate-users", UserCreate);
REG.reg.registerSkin('usercreate-users', 1, /* language=CSS */ `
`);


export class GroupCreate<E extends IRegPointer<ICoreUniverseEnv>> extends UserCreateBase<E> {
	protected async _initialize(init: OUserCreateInit): Promise<void> {
		if (!this.reg) this.reg = REG.createSubReg(this.findReg(init));
		this.id = "groups";
		this.uiContextSgn = "@group#create";
		this.forcedUserDataOnSave.userType = EUserType.group;
		// Form
		this.reg.addToList("userProps:create:groups", 'account', 1, new UserAccountInputArea());
		this.reg.addSvcToList("userProps:create:groups", 'groupName', 1, 'area.users.groupName');
		this.reg.addSvcToList("userProps:create:groups", 'isHidden', 1, 'area.users.isHidden.groups');
		this.reg.addSvcToList("userProps:create:groups", 'groups', 1, 'area.users.groupsmembers.groups.create');
		this.reg.addSvcToList("userProps:create:groups", 'grantedRoles', 1, 'area.users.grantedRoles.groups');
		this.reg.addSvcToList("userProps:create:groups", 'email', 1, 'area.users.email.group');
		return super._initialize(init);
	}

	protected async _doExecuteSave(): Promise<string | null> {
		try {
			const params = Object.assign(FORMS.formToJson(this._form), this.forcedUserDataOnSave);
			let userName = USER.getPrimaryName(params);
			const resp = await this.userSelfSrv.createGroup(params);
			let errorStr = USERSELF.tcheckUserSelfRespError(resp, "");
			if (errorStr === null) {
				POPUP.showNotifInfo(`Le groupe '${userName}' a été créé avec succès.`, this);
				return params.account;
			} else
				await ERROR.show(errorStr ? `La création du groupe '${userName}' a échoué. ${errorStr}` : "La création du groupe a échoué.", new Error(JSON.stringify(resp)));
			return null;
		} catch (e) {
			await ERROR.report(`La création du groupe a échoué. Veuillez réessayer ultérieurement.`, e);
			return null;
		}
	}
}

customElements.define("usercreate-groups", GroupCreate);
REG.reg.registerSkin('usercreate-groups', 1, /* language=CSS */ `
`);